import { useMemo } from "react"

import { HandleAddToCollectionDialog } from "~/components/collections/dialogs/HandleAddToCollectionDialog"
import { HideVariantsDialog } from "~/components/npi/HideVariantsDialog"
import { LifecycleConfirmationDialog } from "~/components/npi/LifecycleConfirmationDialog"
import {
  useSelectedVariantHandlers,
  useSelectedVariantState,
} from "~/context/SelectedVariantsProvider"
import type { INpiVariantSummary } from "~/server/api/routers/npi"

import Commandbar from "./Commandbar"
import ReactivateSkusDialog from "../npi/ReactivateSkusDialog"

type MultiEditNpiPanelProps = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedVariantIds: () => void
  npiContext?: "suggested"
}

export function MultiEditNpiPanel({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedVariantIds,
}: MultiEditNpiPanelProps) {
  const { getSelectedVariants } = useSelectedVariantHandlers<INpiVariantSummary>()
  const { selectedVariantCount: stateCount } = useSelectedVariantState()

  const selectedVariants = useMemo(() => getSelectedVariants(), [getSelectedVariants, stateCount])

  const npiVariantData = useMemo(
    () =>
      selectedVariants.map((v) => ({
        variantId: v.variantId,
        variantName: v.variantName,
        skuIds: v.skuIds,
        hasSalesBasedForecast: v.settings.hasSalesBasedForecast,
        activatedFrom: v.settings.activatedFrom,
        isNpi: v.isNpi,
      })),
    [selectedVariants],
  )

  const hasMultipleProductTypes = useMemo(() => {
    const types = new Set(selectedVariants.map((v) => v.settings.productType))
    return types.size > 1
  }, [selectedVariants])

  return (
    <>
      {selectedVariantCount > 0 && (
        <Commandbar count={selectedVariantCount} onClear={clearSelectedVariantIds}>
          <ReactivateSkusDialog selectedVariantIds={selectedVariantIds} />
          <HandleAddToCollectionDialog
            variantIds={selectedVariantIds}
            npiVariantData={npiVariantData}
            disabled={hasMultipleProductTypes}
            disabledTooltip={
              hasMultipleProductTypes
                ? "Cannot add to collection: selected variants have different product types"
                : undefined
            }
          />
          <LifecycleConfirmationDialog variantIds={selectedVariantIds} field="activatedUntil" />
          <HideVariantsDialog variantIds={selectedVariantIds} />
        </Commandbar>
      )}
    </>
  )
}
